import { IPoint } from '@/version-types';
import { pointsDistance } from '../math-kit';
import { MouseButtonType } from './mouse';

const lastMousePoint: IPoint = { x: 0, y: 0 };

export const getLastMousePoint = () => lastMousePoint;

export const setLastMousePoint = (x: number, y: number) => {
    lastMousePoint.x = x;
    lastMousePoint.y = y;
};

document.addEventListener(
    'mousedown',
    (e: MouseEvent) => {
        lastMousePoint.x = e.x;
        lastMousePoint.y = e.y;
        if (e.button !== MouseButtonType.LEFT) {
            return;
        }
        TouchKit.fireMouseup();
        TouchKit.mousedownEvent = e;
        TouchKit.mousedownEventList.forEach((done) => {
            done(e);
        });
    },
    false,
);

document.addEventListener(
    'mousemove',
    (() => {
        let scheduled = false;
        let lastEvent: any = null;

        return (e) => {
            lastMousePoint.x = e.x;
            lastMousePoint.y = e.y;
            if (e.button !== MouseButtonType.LEFT) {
                return;
            }

            lastEvent = e;

            if (!scheduled) {
                scheduled = true;

                requestAnimationFrame(() => {
                    TouchKit.mousemoveEventList.forEach((done) => {
                        done(lastEvent);
                    });
                    scheduled = false; // 重置标志以便下次使用
                });
            }
        };
    })(),
    false,
);

document.addEventListener(
    'mouseup',
    (e: MouseEvent) => {
        lastMousePoint.x = e.x;
        lastMousePoint.y = e.y;
        if (e.button !== MouseButtonType.LEFT) {
            return;
        }
        // console.log('mouseup');
        TouchKit.mousedownEvent = null;
        TouchKit.mouseupEventList.forEach((done) => {
            done(e);
        });
    },
    false,
);

export interface ITouchKitEvent {
    originEvent: MouseEvent;
    startPoint: IPoint;
    lastPoint: IPoint;
    currentPoint: IPoint;
    distance: number;
    deltaX: number;
    deltaY: number;
    displacementX: number;
    displacementY: number;
    distanceX: number;
    distanceY: number;
    currentTarget: number;
    target: HTMLElement;
    isEnd: boolean;
    isStart: boolean;
    x: number;
    y: number;
}

export interface ITouchKitEventCallback {
    (event: ITouchKitEvent): void;
}

export enum TouchKitEventType {
    Enter,
    Move,
    Leave,
}

export class TouchKit {
    startPoint: IPoint | null = null;
    lastPoint: IPoint | null = null;
    // target: HTMLElement | Document;
    panEventCallback: Function | null = null;
    firstMove: boolean = true;
    isMoving: boolean = false;
    /** 从firstMove开始, 记录this.move的回调次数 */
    moveFnCallCount = 0;
    currentTarget: EventTarget | null = null;

    mousedownHandle: any;
    mousemoveHandle: any;
    mouseupHandle: any;

    static mousedownEvent: MouseEvent | null;
    static mousedownEventList = new Set<Function>();
    static mousemoveEventList = new Set<Function>();
    static mouseupEventList = new Set<Function>();

    static fireMouseup() {
        if (TouchKit.mousedownEvent) {
            TouchKit.mouseupEventList.forEach((done) => {
                done(TouchKit.mousedownEvent);
            });
        }
        TouchKit.mousedownEvent = null;
    }

    bindEvent() {
        this.mousedownHandle = (e: MouseEvent) => this.start(e);
        this.mousemoveHandle = (e: MouseEvent) => this.move(e);
        this.mouseupHandle = (e: MouseEvent) => this.end(e);

        TouchKit.mousedownEventList.add(this.mousedownHandle);
        TouchKit.mousemoveEventList.add(this.mousemoveHandle);
        TouchKit.mouseupEventList.add(this.mouseupHandle);

        if (TouchKit.mousedownEvent) {
            this.mousedownHandle(TouchKit.mousedownEvent);
        }

        // console.log(TouchKit.mousedownEventList);
    }

    unbindEvent() {
        TouchKit.mousedownEventList.delete(this.mousedownHandle);
        TouchKit.mousemoveEventList.delete(this.mousemoveHandle);
        TouchKit.mouseupEventList.delete(this.mouseupHandle);
        // console.log(TouchKit.mousedownEventList);
        this.mousedownHandle = null;
        this.mousemoveHandle = null;
        this.mouseupHandle = null;
    }

    start(evt: MouseEvent) {
        this.currentTarget = evt.target;

        this.startPoint = {
            x: evt.x,
            y: evt.y,
        };

        this.lastPoint = {
            x: evt.x,
            y: evt.y,
        };
    }

    move(evt: MouseEvent) {
        if (
            this.panEventCallback === null ||
            this.startPoint === null ||
            this.lastPoint === null ||
            TouchKit.mousedownEvent === null
        )
            return;

        const isStart = this.firstMove;
        const currentPoint = {
            x: evt.x,
            y: evt.y,
        };

        /**
         * 在Mac触控板上，即使手指没有移动，也有可能触发mousemove事件
         * 这可能是由于触控板的精确度和敏感度造成的。当手指接触触控板时，即使微微晃动或触摸压力发生微小变化，也会被视为移动。
         * 所以需要过滤掉
         */
        if (
            isStart &&
            TouchKit.mousedownEvent.clientX === currentPoint.x &&
            TouchKit.mousedownEvent.clientY === currentPoint.y
        )
            return;

        const touchKitEvent = {
            originEvent: evt,
            startPoint: this.startPoint,
            lastPoint: this.lastPoint,
            currentPoint: currentPoint,
            distance: pointsDistance(this.startPoint, currentPoint),
            deltaX: currentPoint.x - this.lastPoint.x,
            deltaY: currentPoint.y - this.lastPoint.y,
            displacementX: currentPoint.x - this.startPoint.x,
            displacementY: currentPoint.y - this.startPoint.y,
            distanceX: Math.abs(currentPoint.x - this.startPoint.x),
            distanceY: Math.abs(currentPoint.y - this.startPoint.y),
            target: this.currentTarget,
            isEnd: false,
            x: evt.x,
            y: evt.y,
            isStart,
            // dela
        };

        this.panEventCallback(touchKitEvent);

        this.lastPoint = {
            x: evt.x,
            y: evt.y,
        };

        this.isMoving = true;
        this.firstMove = false;
    }

    end(evt: MouseEvent) {
        if (
            this.startPoint === null ||
            this.panEventCallback === null ||
            this.lastPoint === null ||
            this.currentTarget === null
        ) {
            return;
        }

        const currentPoint = {
            x: evt.x,
            y: evt.y,
        };

        const touchKitEvent = {
            originEvent: evt,
            startPoint: this.startPoint,
            lastPoint: this.lastPoint,
            currentPoint: currentPoint,
            distance: pointsDistance(this.startPoint, currentPoint),
            deltaX: currentPoint.x - this.lastPoint.x,
            deltaY: currentPoint.y - this.lastPoint.y,
            displacementX: currentPoint.x - this.startPoint.x,
            displacementY: currentPoint.y - this.startPoint.y,
            distanceX: Math.abs(currentPoint.x - this.startPoint.x),
            distanceY: Math.abs(currentPoint.y - this.startPoint.y),
            currentTarget: evt.currentTarget,
            target: this.currentTarget,
            isEnd: true,
            isStart: false,
            x: evt.x,
            y: evt.y,
        };

        // console.log(touchKitEvent, evt);

        if (this.isMoving) {
            this.panEventCallback(touchKitEvent);
        }

        this.startPoint = null;
        this.firstMove = true;
        this.currentTarget = null;
        this.isMoving = false;
    }

    on(eventName: TouchKitEventType, callback: ITouchKitEventCallback) {
        if (eventName === TouchKitEventType.Move) {
            this.panEventCallback = callback;
            this.bindEvent();
        }
    }

    off(eventName: TouchKitEventType) {
        if (eventName === TouchKitEventType.Move) {
            this.unbindEvent();
            this.panEventCallback = null;
        }
    }
}

/**
 * 转换距离
 *
 * @param event - 触摸事件
 * @param scale - 缩放比例
 */
export function toTouchKitDistance(event: ITouchKitEvent, scale: number) {
    // 没有位移的时候，最后距离也为 0
    if (!event.distance || !event.distanceX) {
        return 0;
    }

    const { displacementX, distance } = event;
    const direction = displacementX / Math.abs(displacementX);

    return (distance / scale) * direction;
}
